import { hotkeyHint } from "@/hooks/useCoverageBetaHotkeys";
import { useCoverageBetaStore } from "@/store/useCoverageBetaStore";
import { OoSParcelSchema } from "@augmodo/lebron-browser-client";
import { Button, Tooltip } from "@mantine/core";
import { IconBan } from "@tabler/icons-react";
import { ConfirmProductSelectionPanel } from "../../confirm-product-selection/ConfirmProductSelectionPanel";
import { CoverageBetaQaPanel } from "../../coverage-beta-qa/CoverageBetaQaPanel";
import { SelectedProduct } from "./selected-product/SelectedProduct";

export const COVERAGE_BETA_RIGHT_FOOTER_HEIGHT = 200;

interface Props {
  parcelResp: OoSParcelSchema | undefined;
}

export const CoverageBetaRightFooter = (props: Props) => {
  const { parcelResp } = props;
  const activeEmptyAreaIdx = useCoverageBetaStore((s) => s.activeEmptyAreaIdx);
  const idxToFeEmptyArea = useCoverageBetaStore((s) => s.idxToFeEmptyArea);
  const confirming = useCoverageBetaStore((s) => s.confirming);
  const toggleEmptyAreaNoMatch = useCoverageBetaStore(
    (s) => s.toggleEmptyAreaNoMatch,
  );
  const isReadOnly = useCoverageBetaStore((s) => s.isReadOnly);
  const isQaMode = useCoverageBetaStore((s) => s.isQaMode);
  const activeEmptyArea = idxToFeEmptyArea?.[activeEmptyAreaIdx];

  // Coverage Beta QA replaces the normal edit-mode footer entirely with the
  // Box Review Panel — verdicts, not product editing.
  if (isQaMode) {
    return <CoverageBetaQaPanel />;
  }

  // While confirming, the footer hosts the inline confirm-product step.
  if (confirming != null) {
    return (
      <div className="h-full p-2">
        <ConfirmProductSelectionPanel parcelResp={parcelResp} />
      </div>
    );
  }

  if (!activeEmptyArea || activeEmptyArea.isDeleted) {
    return <div className="h-full p-2" />;
  }

  const selectedProducts = activeEmptyArea.selectedProducts;
  const isNoMatch = activeEmptyArea.isValid && selectedProducts.length === 0;
  // The button toggles, so the tooltip names the action the click will perform.
  const noMatchLabel = isNoMatch
    ? "Clear the no-match mark on this empty area"
    : "Mark this empty area as having no matching product";

  return (
    <div className="h-full flex flex-col gap-2 p-2">
      <div className="flex items-center gap-2">
        <div className="text-xs text-gray-500 font-semibold">
          Empty area {activeEmptyAreaIdx}
        </div>
        <div className="ml-auto flex items-center gap-1">
          {/* Per-product Confirm / Change / remove now live on each row in the
              selected-products list below, so individual selections can be
              actioned. Only the area-level "No match" stays here. */}
          {!isReadOnly && (
            <Tooltip label={`${noMatchLabel} (${hotkeyHint("no_match")})`}>
              <Button
                size="compact-xs"
                variant={isNoMatch ? "filled" : "light"}
                color="gray"
                leftSection={<IconBan size={14} />}
                onClick={() => toggleEmptyAreaNoMatch(activeEmptyAreaIdx)}
              >
                No match
              </Button>
            </Tooltip>
          )}
        </div>
      </div>

      <div className="space-y-1 overflow-y-auto flex-1">
        {selectedProducts.map((product, selectedProductIdx) => {
          const key =
            product.source === "reference"
              ? `ref-${product.id}-${product.referenceImgIdx}-${product.referenceProductDetectionIdx}`
              : `plano-${product.id}-${product.planogram_id}`;
          return (
            <SelectedProduct
              key={key}
              product={product}
              selectedProductIdx={selectedProductIdx}
            />
          );
        })}
      </div>
    </div>
  );
};
